import { useState, useRef, useEffect } from 'react';
import {
  Search, Send, Paperclip, Smile, User, MoreVertical, Phone,
  Video, Trash, AlertTriangle, Archive, BellOff, UserPlus,
  Download, Link, Settings,
  FileText,
  X,
  MessageSquare,
} from 'lucide-react';

import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger
} from "@/components/ui/tooltip";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/components/ui/use-toast";
import { authHeaders, Axios, Chat_Axios, socket } from '@/utils/axios';
import { useUserStore } from '@/store/userStore';
import { API_END_POINTS } from '@/utils/routes';
import { useSearchParams, Link as DirectLink, useNavigate } from 'react-router-dom';
import { selectUnreadForConversation, useMessageStore } from '@/store/messageStore';
import { useEnterpriseStore } from '@/store/enterpriseStore';
import { capitalizeFirst, getImageUrl } from '@/lib/utils';
import { Role } from '@/utils/constant';
import { useFreelancerStore } from '@/store/freelancerStore';

const EmojiPicker = ({ onEmojiSelect }: { onEmojiSelect: (emoji: string) => void }) => {
  const emojis = ["😀", "👍", "❤️", "🎉", "🔥", "👋", "😂", "😊", "🙌", "👏"];

  return (
    <div className="grid grid-cols-5 gap-2 p-3">
      {emojis.map(emoji => (
        <button
          key={emoji}
          onClick={() => onEmojiSelect(emoji)}
          className="text-xl hover:bg-gray-100 p-2 rounded cursor-pointer"
        >
          {emoji}
        </button>
      ))}
    </div>
  );
};

export default function Messages() {

  const [contacts, setContacts] = useState<any[]>([]);
  const [selectedContact, setSelectedContact] = useState<any | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);
  const [messageContent, setMessageContent] = useState("");
  const [messages, setMessages] = useState<any[]>([]);
  const chatContainerRef = useRef<HTMLDivElement | null>(null);
  const { toast } = useToast();
  const { user, updateUnReadMessages } = useUserStore();
  const [searchParams] = useSearchParams();
  const conversationId = searchParams.get("conversationId")
  const [showJobDescription, setShowJobDescription] = useState(false)

  const [files, setFiles] = useState<File[]>([]);
  const [attachments, setAttachments] = useState<
    { originalname: string; mimetype: string; buffer: string }[]
  >([]);

  const fileInputRef = useRef(null);
  const unreadCounts = useMessageStore((state) => state.unreadCounts);

  const { profile: enterpriseProfile } = useEnterpriseStore();
  const { profile: FreelancerProfile } = useFreelancerStore();

  const navigate = useNavigate();

  useEffect(() => {
    const fetchContacts = async () => {
      try {
        const { data } = await Chat_Axios.get(
          `${API_END_POINTS.LIST_CONVERSATIONS}`,
          authHeaders(user?.accessToken)
        );

        const conversations = Array.isArray(data) ? data : [data];
        const mapped = conversations.flatMap((conv) =>
          conv.participants.map((p: any) => ({
            conversationId: conv._id,
            participantId: p.userId,
            name: p.name || "Unknown",
            avatar: p.avatar || "",
            lastActive: conv.updatedAt,
            unreadMessages: conv.unreadCount || 0,
            JobdetailsMessage: conv.jobDetails,
            jobId: conv.jobId
          }))
        );

        setContacts(mapped);
        if (conversationId) {
          const matchConversation = mapped?.find(value => value?.conversationId === conversationId)
          if (matchConversation) {
            handleContactSelect(matchConversation)
          }
        }
      } catch (error) {
        console.error("Failed to fetch contacts:", error);
      }
    };

    fetchContacts();
  }, [user?.accessToken]);

  useEffect(() => {
    const totalUnreadMessages = contacts.reduce(
      (sum, m) => sum + (m.unreadMessages || 0),
      0
    );
    updateUnReadMessages(totalUnreadMessages);
  }, [contacts]);

  useEffect(() => {
    socket.on("message:new", (message) => {
      if (message.conversationId === selectedContact?.conversationId) {
        setMessages((prev) => [...prev, message]);
        scrollToBottom();
      }
    });

    return () => {
      socket.off("message:new");
    };
  }, [selectedContact]);

  const fetchMessages = async (conversationId: string) => {
    try {
      const { data } = await Chat_Axios.get(
        `${API_END_POINTS.GET_MESSAGE}/${conversationId}`,
        authHeaders(user?.accessToken)
      );

      const sortedMessages = [...data].sort(
        (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      );

      setMessages(sortedMessages);

      setTimeout(() => scrollToBottom(), 100);
    } catch (error) {
      console.error("Failed to fetch messages:", error);
    }
  };

  const handleFilesSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!selectedContact) {
      toast({
        title: "Select a conversation first",
        variant: "destructive",
      });
      return;
    }

    const selectedFiles = event.target.files ? Array.from(event.target.files) : [];
    if (selectedFiles.length === 0) return;

    // Convert files to base64 attachments
    const newAttachments = await Promise.all(
      selectedFiles.map((file) => {
        return new Promise<{ originalname: string; mimetype: string; buffer: string }>((resolve) => {
          const reader = new FileReader();
          reader.onload = () => {
            const base64Data = (reader.result as string).split(",")[1];
            resolve({
              originalname: file.name,
              mimetype: file.type,
              buffer: base64Data,
            });
          };
          reader.readAsDataURL(file);
        });
      })
    );

    setFiles((prev) => [...prev, ...selectedFiles]);
    setAttachments((prev) => [...prev, ...newAttachments]);
  };

  const handleSendMessage = () => {
    if (!selectedContact) return;
    if (!messageContent.trim() && attachments.length === 0) return;

    // Create an optimistic message ID to track duplicates later
    const tempMessageId = Date.now().toString();

    // Construct message object to send
    const messageToSend = {
      conversationId: selectedContact.conversationId,
      sender: user.userId,
      content: messageContent,
      attachments,
      tempId: tempMessageId,  // add a temporary ID for tracking
    };

    // Emit message through socket
    socket.emit("message:send", messageToSend);

    // Optimistic UI update: append message locally immediately
    const optimisticMessage = {
      _id: tempMessageId,
      sender: { _id: user.userId },
      content: messageContent,
      attachments: files,
      createdAt: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, optimisticMessage]);

    // Reset inputs
    setMessageContent("");
    setFiles([]);
    setAttachments([]);

    scrollToBottom();
  };

  const handleEmojiSelect = (emoji: string) => {
    setMessageContent((prevContent) => prevContent + emoji);
    setIsEmojiPickerOpen(false);
  };

  const filteredContacts = contacts.filter((c) =>
    c.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleContactSelect = async (contact: any) => {
    setSelectedContact(contact);
    await fetchMessages(contact.conversationId);

    socket.emit("conversation:read", {
      conversationId: contact.conversationId,
      userId: user.userId,
    });
    setContacts((prev) =>
      prev.map((c) =>
        c.conversationId === contact.conversationId
          ? { ...c, unreadMessages: 0 }
          : c
      )
    );

    useMessageStore.getState().setUnreadCount(contact.conversationId, 0);

    if (contact.JobdetailsMessage) {
      setShowJobDescription(true);
    } else {
      setShowJobDescription(false);
    }
  };

  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  };

  const handleArchiveChat = () => {
    toast({
      title: "Chat Archived",
      description: `You have archived the chat with ${selectedContact.name}.`,
    });
  };

  const handleDeleteChat = async (selectedContact) => {
    try {
      await Axios.delete(
        `${API_END_POINTS.LIST_CONVERSATIONS}/${selectedContact?.conversationId}`,
        authHeaders(user.accessToken)
      );
      const updateChatUserList = contacts?.filter(value => value?.conversationId !== selectedContact?.conversationId);
      setContacts(updateChatUserList);
      setSelectedContact(null);
      toast({
        title: "Chat Deleted",
        description: `You have deleted the chat with ${selectedContact.name}.`,
      });
    } catch (err: any) {
      toast({
        title: "Failed to delete chat. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleMuteChat = () => {
    toast({
      title: "Chat Muted",
      description: `You have muted notifications from ${selectedContact.name}.`,
    });
  };

  const handleAddUser = () => {
    toast({
      title: "Add User",
      description: `You have added a user to the chat with ${selectedContact.name}.`,
    });
  };

  const handleDownloadChat = () => {
    toast({
      title: "Download Chat",
      description: `You have downloaded the chat with ${selectedContact.name}.`,
    });
  };

  const handleCopyChatLink = () => {
    toast({
      title: "Copy Chat Link",
      description: `You have copied the chat link with ${selectedContact.name}.`,
    });
  };

  const handleBlockUser = () => {
    toast({
      title: "Block User",
      description: `You have blocked ${selectedContact.name}.`,
    });
  };

  const handleReportUser = () => {
    toast({
      title: "Report User",
      description: `You have reported ${selectedContact.name}.`,
    });
  };

  const handleSettings = () => {
    toast({
      title: "Settings",
      description: `You have opened the settings.`,
    });
  };

  const formatTimestamp = (iso: string) => {
    const date = new Date(iso);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const extractFilenameFromUrl = (url) => {
    if (!url) return 'file';
    const cleanUrl = url.split('?')[0];
    const parts = cleanUrl.split('/');
    return parts[parts.length - 1] || 'file';
  };


  const handleDownload = async (file, fileUrl) => {
    const fileName = extractFilenameFromUrl(fileUrl) || file.originalname || file.name || 'file';

    try {
      if (file instanceof File) {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(file);
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else if (fileUrl.startsWith('http')) {
        const response = await fetch(fileUrl);
        if (!response.ok) throw new Error('Network response was not ok');
        const blob = await response.blob();

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        alert('Cannot download this file');
      }
    } catch (err) {
      console.error('Download error:', err);
      alert('Failed to download file');
    }
  };

  return (
    <div className="container mx-auto max-w-6xl py-6 px-4 sm:px-6 lg:px-8 bg-gray-100 dark:bg-gray-900 max-h-screen">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden flex flex-col md:flex-row min-h-[calc(100vh-9rem)]">
        <div className="w-full md:w-1/4 lg:w-1/5 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col overflow-y-auto">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-3">
              <Avatar>
                <AvatarImage src={getImageUrl(user.role === Role.ENTERPRISE ? enterpriseProfile?.logo : FreelancerProfile?.profilePicture)} alt={user.role === Role.ENTERPRISE ? enterpriseProfile?.companyName : FreelancerProfile?.fullName} />
                <AvatarFallback>{capitalizeFirst(user.role === Role.ENTERPRISE ? enterpriseProfile?.companyName : FreelancerProfile?.fullName)?.charAt(0)}</AvatarFallback>
              </Avatar>
              <div>
                <div className="font-medium text-gray-900 dark:text-white">{capitalizeFirst(user.role === Role.ENTERPRISE ? enterpriseProfile?.companyName : FreelancerProfile?.fullName)}</div>
                <div className="text-sm text-gray-500 dark:text-gray-400">Online</div>
              </div>
            </div>
          </div>

          <div className="p-3">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <Search className="h-5 w-5 text-gray-500 dark:text-gray-400" />
              </div>
              <Input
                type="search"
                placeholder="Search contacts..."
                className="pl-10 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="overflow-y-auto flex-1" style={{ maxHeight: "calc(100vh - 20rem)" }}>
            <ul className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredContacts.map((contact) => {
                const unread = unreadCounts[contact.conversationId] || 0;

                return (
                  <li
                    key={`${contact.conversationId}-${contact.participantId}`}
                    className={`p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 ${selectedContact?.conversationId === contact.conversationId &&
                      selectedContact?.participantId === contact.participantId
                      ? "bg-gray-100 dark:bg-gray-600"
                      : ""
                      }`}
                    onClick={() => handleContactSelect(contact)}
                  >
                    <div className="flex items-center space-x-3">
                      <div className="relative">
                        <Avatar>
                          <AvatarImage src={getImageUrl(contact.avatar)} alt={contact.name} />
                          <AvatarFallback>{capitalizeFirst(contact.name.charAt(0))}</AvatarFallback>
                        </Avatar>
                        {contact.isOnline && (
                          <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border border-white dark:border-gray-800"></span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{capitalizeFirst(contact.name)}</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{contact.lastMessage}</p>
                      </div>

                      {/* 👇 Replace static value with Zustand-managed value */}
                      {unread > 0 && (
                        <Badge className="ml-2">{unread}</Badge>
                      )}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      {formatTimestamp(contact.lastActive)}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>

        <div className="flex-1 flex flex-col bg-gray-50 dark:bg-gray-800">
          {selectedContact ? (
            <>
              <div className="bg-white dark:bg-gray-700 border-b border-gray-200 dark:border-gray-700 p-4 flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Avatar>
                    <AvatarImage src={getImageUrl(selectedContact.avatar)} alt={capitalizeFirst(selectedContact.name)} />
                    <AvatarFallback>{capitalizeFirst(selectedContact.name.charAt(0))}</AvatarFallback>
                  </Avatar>
                  <div>
                    <div className="font-medium text-gray-900 dark:text-white">{capitalizeFirst(selectedContact.name)}</div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <Phone className="h-5 w-5" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Call</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <Video className="h-5 w-5" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Video Call</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <MoreVertical className="h-5 w-5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={handleArchiveChat}>
                        <Archive className="h-4 w-4 mr-2" />
                        Archive
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleDeleteChat(selectedContact)}>
                        <Trash className="h-4 w-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={handleMuteChat}>
                        <BellOff className="h-4 w-4 mr-2" />
                        Mute
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={handleAddUser}>
                        <UserPlus className="h-4 w-4 mr-2" />
                        Add User
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuLabel>More Actions</DropdownMenuLabel>
                      <DropdownMenuItem onClick={handleDownloadChat}>
                        <Download className="h-4 w-4 mr-2" />
                        Download Chat
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={handleCopyChatLink}>
                        <Link className="h-4 w-4 mr-2" />
                        Copy Chat Link
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={handleBlockUser}>
                        <User className="h-4 w-4 mr-2" />
                        Block User
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={handleReportUser}>
                        <AlertTriangle className="h-4 w-4 mr-2" />
                        Report User
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={handleSettings}>
                        <Settings className="h-4 w-4 mr-2" />
                        Settings
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>

              <div
                ref={chatContainerRef}
                className="flex-1 p-2 overflow-y-auto space-y-2 relative"
                style={{ maxHeight: "calc(100vh - 18rem)" }}
              >
                {!!showJobDescription && (
                  <div className="sticky top-0 z-10 bg-gray-100  flex justify-between p-2 text-sm font-medium w-full border rounded border-gray-300 h-16 shadow-sm">
                    <div>
                      <DirectLink
                        to={`/${user?.role === 'freelancer' ? 'project-details' : 'job-details'}/${selectedContact?.jobId}`}
                        key={selectedContact?.jobId}
                        className="hover:scale-[1.01] transition-transform duration-200"
                      >
                        <p className="underline">{selectedContact?.JobdetailsMessage?.title}</p>
                      </DirectLink>
                      <span>{selectedContact?.JobdetailsMessage?.category}</span>
                    </div>
                    <button onClick={() => setShowJobDescription(false)}>
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                )}
                {messages.map((message) => (
                  <div
                    key={message._id}
                    className={`p-4 flex ${message.sender?._id === user.userId ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-xs rounded-xl px-4 py-2 ${message.sender?._id === user.userId ? "bg-blue-500 text-white" : "bg-gray-200 dark:bg-gray-600 dark:text-white"
                        }`}
                    >
                      {/* Render attachments if any */}
                      {message.attachments.map((file, idx) => {

                        const baseUrl = Chat_Axios.defaults.baseURL;
                        const fileUrl = file.url
                          ? `${baseUrl}${file.url}`
                          : file instanceof File
                            ? URL.createObjectURL(file)
                            : '';

                        const isImage = (
                          (file.mimetype && file.mimetype.startsWith("image/")) ||
                          (file.type && file.type.startsWith("image/")) ||
                          /\.(jpg|jpeg|png|gif|webp|svg|bmp|tiff?)$/i.test(file.url || file.name || '')
                        );

                        const fileName = file.originalname || file.name || 'file';

                        return (
                          <div key={idx} className="relative group w-24 h-24 mb-1 rounded-md overflow-hidden border shadow-sm">
                            {isImage ? (
                              <img src={fileUrl} alt={fileName} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex flex-col items-center justify-center bg-gray-100 text-gray-700 text-sm font-medium">
                                <FileText color="#3B82F6" size={24} />
                                <span className='w-full px-1 text-xs text-center truncate'>{fileName}</span>
                              </div>
                            )}

                            {/* Hover Download Button */}
                            <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity duration-200">
                              <button
                                onClick={() => handleDownload(file, fileUrl)}
                                className="bg-white text-black text-xs px-2 py-1 rounded hover:bg-gray-200"
                              >
                                <Download className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        );
                      })}

                      {/* Render text content */}
                      {message.content && <p className='text-right'>{message.content}</p>}

                      <div className="text-xs mt-1 text-right">
                        {formatTimestamp(message.createdAt)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-white dark:bg-gray-700 border-t border-gray-200 dark:border-gray-700 p-4 space-y-2">
                {/* Inline image previews */}
                {files.length > 0 && (
                  <div className="flex flex-wrap gap-2 justify-end">
                    {files.map((file, idx) => {
                      const url = URL.createObjectURL(file);
                      const fileName = file.name || "file";
                      const fileType = file.type || "";
                      const ext = fileName.split(".").pop()?.toLowerCase();

                      const isImage =
                        fileType.startsWith("image/") ||
                        /\.(jpg|jpeg|png|gif|webp|svg|bmp|tiff?)$/i.test(fileName);

                      return (
                        <div
                          key={idx}
                          className="relative w-20 h-20 rounded overflow-hidden border border-gray-300 dark:border-gray-600 flex flex-col items-center justify-between p-1 text-[10px] bg-white dark:bg-gray-900"
                          title={fileName}
                        >
                          {isImage ? (
                            <img
                              src={url}
                              alt={fileName}
                              className="w-full h-full object-cover rounded"
                              onLoad={() => URL.revokeObjectURL(url)}
                            />
                          ) : (
                            <div className="flex flex-col items-center justify-center h-full w-full text-gray-700 dark:text-gray-300">
                              <FileText color="#3B82F6" size={24} />
                              <span className="text-center truncate w-full">{fileName}</span>
                            </div>
                          )}

                          {/* Delete button */}
                          <button
                            onClick={() => {
                              setFiles((prev) => prev.filter((_, i) => i !== idx));
                              setAttachments((prev) => prev.filter((_, i) => i !== idx));
                            }}
                            className="absolute top-0 right-0 bg-black bg-opacity-50 text-white rounded-bl text-xs w-5 h-4 flex items-center justify-center hover:bg-red-600"
                            type="button"
                            aria-label={`Remove ${fileName}`}
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Input bar */}
                <div className="flex items-center space-x-3">
                  <Button variant="ghost" size="icon" onClick={() => setIsEmojiPickerOpen(!isEmojiPickerOpen)}>
                    <Smile className="h-5 w-5" />
                  </Button>

                  <input
                    type="file"
                    multiple
                    ref={fileInputRef}
                    style={{ display: "none" }}
                    onChange={handleFilesSelected}
                    accept="*"
                  />

                  <Button variant="ghost" size="icon" onClick={() => fileInputRef.current?.click()}>
                    <Paperclip className="h-5 w-5" />
                  </Button>
                  <Input
                    placeholder="Type a message..."
                    className="rounded-md flex-1 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                    value={messageContent}
                    onChange={(e) => setMessageContent(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleSendMessage();
                      }
                    }}
                  />
                  <Button onClick={handleSendMessage}>
                    <Send className="h-5 w-5 rotate-90" />
                  </Button>
                </div>

                {/* Emoji Picker */}
                {isEmojiPickerOpen && <EmojiPicker onEmojiSelect={handleEmojiSelect} />}
              </div>

            </>
          ) : (contacts.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center p-6">
              <div className="text-gray-400 dark:text-gray-500">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-12 w-12 mx-auto mb-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 10h.01M12 10h.01M16 10h.01M21 16.5a2.5 2.5 0 01-2.5 2.5H7l-4 4V5.5A2.5 2.5 0 015.5 3h13A2.5 2.5 0 0121 5.5v11z"
                  />
                </svg>

                <h2 className="text-lg font-medium text-gray-700 dark:text-gray-300">
                  Find Freelancers to Chat
                </h2>

                <p className="text-sm text-gray-500 dark:text-gray-400 mt-2 max-w-sm mx-auto">
                  You don’t have any contacts yet. Browse our freelancers and start a conversation instantly.
                </p>

                <div className="mt-4 w-fit max-w-xs mx-auto">
                  <Button
                  onClick={() => navigate('/freelancers')}
                    className="flex items-center justify-center w-full bg-gradient-to-r from-sky-600 to-sky-700 hover:from-sky-700 hover:to-sky-800 shadow-md hover:shadow-sky-200/40 transition-all duration-300 group"
                  >
                    <MessageSquare className="h-4 w-4 mr-2 group-hover:scale-110 transition-transform duration-300" />
                    Find Freelancers
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-center p-6">
              <div className="text-gray-400 dark:text-gray-500">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M21 16.5a2.5 2.5 0 01-2.5 2.5H7l-4 4V5.5A2.5 2.5 0 015.5 3h13A2.5 2.5 0 0121 5.5v11z" />
                </svg>
                <h2 className="text-lg font-medium text-gray-700 dark:text-gray-300">
                  Welcome to Messages
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-2 max-w-sm mx-auto">
                  View your contacts and conversations here. Select a contact from the list to start chatting instantly.
                </p>
                <p></p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
